import React, { Component, Fragment, createRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { JobService, OfficerService, ApplicantService } from "@services";
import utils from "@utils";
import {
	JOB_ASSIGNMENT_ERROR_TYPES,
	OFFICER_STATUSES,
	RECURRENCE_CONFLICT_TYPES,
} from "@constants";
import { AssignmentConfirmationModal, ConflictConfirmationModal } from "./components";
import Modal from "../Modal/Modal";
import Loader from "../Loader/Loader";
import Switch from "../Switch/Switch";
import Tooltip from "../Tooltip/Tooltip";
import ScrollDetect from "../ScrollDetect/ScrollDetect";
import Image from "../Image/Image";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import UnassignOfficerModal from "../UnassignOfficerModal/UnassignOfficerModal";
import ReactSelect from "../ReactSelect/ReactSelect";

import "./ManualAssignModal.scss";

let cancel;
class ManualAssignModal extends Component {
	constructor(props) {
		super(props);

		this.initialUnassignModal = {
			show: false,
			officerId: undefined,
			officerName: undefined,
			recurringAssignment: false,
			recurrenceRule: undefined,
		};

		this.initialAssignConfirmModal = {
			show: false,
			recurrenceRule: {},
			officer: {},
		};

		this.initialConflictConfirmModal = {
			show: false,
			userId: undefined,
			AssignmentRepeatsOn: "",
			updateAllUpcoming: false,
			ignoreMaxWeeklyHrs: false,
			conflictType: undefined,
			jobEvents: [],
		};

		this.initialState = {
			loading: false,
			hidden: false,
			position: { value: "", label: "" },
			assignedOfficers: [],
			appliedOfficers: [],
			searchRKOfficers: true,
			RKOfficerFilter: "",
			prevSearchTerm: "",
			RKOfficers: {
				loading: false,
				searched: false,
				pageNumber: 1,
				results: [],
				hasMore: false,
			},
			unassignModal: { ...this.initialUnassignModal },
			assignConfirmModal: { ...this.initialAssignConfirmModal },
			conflictConfirmModal: { ...this.initialConflictConfirmModal },
			NonRKAssign: "",
			activeTab: "assigned",
			includeNonJurisdiction: false,
			assignmentsChanged: false,
			totalExternalOfficers: 0,
			alreadyOverrideUserScheduleConflict: false,
		};

		this.state = { ...this.initialState };

		this.confirmationModal = createRef();
		this.reassignConfirmationModal = createRef();
	}

	componentDidUpdate({ show: prevShow }) {
		const { show } = this.props;
		if (prevShow !== show && show) {
			this.onModalVisible();
		}
	}

	/** @todo Remove this method from `componentDidUpdate` and call it as the `onEntered` prop of `<Modal>` */
	onModalVisible = () => {
		const { positions, defaultPosition, isLEA } = this.props;
		let { Id: value, Title: label } = positions[0];
		if (defaultPosition !== undefined) {
			({ Id: value, Title: label } = positions.find(pos => pos.Id === defaultPosition));
		}
		let totalExternalOfficers = 0;
		positions.forEach(pos => {
			totalExternalOfficers += pos?.ExternalOfficers?.length || 0;
		});
		this.setState(
			{
				position: { value, label },
				includeNonJurisdiction: !isLEA,
				totalExternalOfficers,
			},
			() => this.getAssignedAndAppliedOfficers(),
		);
	};

	/** Retrieves all assigned officers and applicants for the currently selected position */
	getAssignedAndAppliedOfficers = () => {
		const { position } = this.state;
		const assignedPromise = new Promise(resolve => {
			JobService.getAssignedOfficersByPositionId(position.value)
				.then(({ data: { AssignedOfficers, ExternalOfficers } }) => {
					const externalOfficers = ExternalOfficers.map(officer => ({
						...officer,
						isExternal: true,
					}));
					resolve([...AssignedOfficers, ...externalOfficers]);
				})
				.catch(() => resolve([]));
		});
		const appliedPromise = new Promise(resolve => {
			JobService.getApplicantsByPositionId(position.value)
				.then(({ data }) => resolve(data))
				.catch(() => resolve([]));
		});
		Promise.all([assignedPromise, appliedPromise]).then(([assignedOfficers, appliedOfficers]) =>
			this.setState({ assignedOfficers, appliedOfficers }),
		);
	};

	/** Update the preference for including officers outside the current user's jurisdiction in searches */
	toggleOwnOfficers = () => {
		const { RKOfficers } = this.state;
		this.setState(
			prevState => ({ includeNonJurisdiction: !prevState.includeNonJurisdiction }),
			() => {
				if (RKOfficers.searched) this.searchOfficers();
			},
		);
	};

	/**
	 * Handles the current user selecting a new position to view assigments for
	 * @param {{ value: *, label : String}} event The object returned when a `ReactSelect` selection is updated
	 */
	handlePositionChange = event => {
		if (!event) {
			this.setState({ position: { value: "", label: "" } });
			return;
		}
		this.setState({ position: event }, () => this.getAssignedAndAppliedOfficers());
	};

	/**
	 * Handles changes to text inputs within the modal
	 * @param {Event} event The HTML event handled by this method
	 */
	handleInputChange = event => {
		const {
			target: { name, value },
		} = event;
		this.setState({ [name]: value });
	};

	/** Retrieves a list of officers whose information matches the current search term */
	getFilteredOfficers = () => {
		const {
			RKOfficerFilter,
			RKOfficers: { pageNumber },
			includeNonJurisdiction,
		} = this.state;
		const queryParams = {
			pageNumber,
			includeNonJurisdiction,
			displayCount: 25,
			includeDeleted: false,
			jobCount: false,
			totalJobsWorked: true,
			upcomingJobsScheduled: true,
			vettedStatus: [2, 6],
		};
		const body = {
			firstName: RKOfficerFilter,
			lastName: RKOfficerFilter,
			emailAddress: RKOfficerFilter,
			mobilePhone: "",
			zipCode: "",
		};
		if (cancel) cancel();
		OfficerService.search(queryParams, body, c => {
			cancel = c;
		})
			.then(({ data: { ItemList } }) => {
				cancel = undefined;
				this.setState(prevState => ({
					RKOfficers: {
						loading: false,
						searched: true,
						pageNumber: prevState.RKOfficers.pageNumber + 1,
						results:
							pageNumber === 1
								? ItemList
								: [...prevState.RKOfficers.results, ...ItemList],
						hasMore: ItemList.length >= 25,
					},
				}));
			})
			.catch(() => {
				this.setState(prevState => ({
					RKOfficers: {
						...prevState.RKOfficers,
						loading: false,
						searched: true,
						hasMore: false,
					},
				}));
			});
	};

	/**
	 * Applies the current text in the search field and searches for officers whose information matches that value.
	 * If the text field is empty, any existing search results are cleared
	 */
	searchOfficers = () => {
		const { RKOfficerFilter } = this.state;
		if (RKOfficerFilter === "") {
			this.clearSearchResults();
			return;
		}
		this.setState(
			prevState => ({
				prevSearchTerm: RKOfficerFilter,
				RKOfficers: { ...prevState.RKOfficers, pageNumber: 1, loading: true },
			}),
			() => this.getFilteredOfficers(),
		);
	};

	/**
	 * Determines if an officer is assigned to recurring instances of the current job in the same position
	 * @param {Number} officerId The ID number for a RollKall officer
	 * @returns {Boolean} `true` if the officer is assigned to recurring instances of the job; `false` otherwise
	 */
	isRecurringAssignment = officerId => {
		const { assignedOfficers } = this.state;

		const officer = assignedOfficers.find(o => o.UserId === officerId);
		if (officer) {
			if (officer) {
				const { RecurrenceRuleJobAssignment } = officer;
				return (
					RecurrenceRuleJobAssignment &&
					Object.values(RecurrenceRuleJobAssignment).length > 0
				);
			}
		}
		return false;
	};

	/**
	 * Determines if an officer is currently assigned to the selected position
	 * @param {Number} officerId The ID number for a RollKall officer
	 * @returns {Boolean} `true` if the officer is currently assigned to the selected position; `false` otherwise
	 */
	isOfficerAssigned = officerId => {
		const { assignedOfficers } = this.state;
		return assignedOfficers.some(officer =>
			officer.isExternal ? officer.Id === officerId : officer.UserId === officerId,
		);
	};

	/**
	 * Renders a label with a text description indicating the officer's status on the selected position
	 * @param {Number} type The status ID for the officer
	 */
	renderOfficerStatus = type => {
		const officerStatus =
			Object.values(OFFICER_STATUSES).find(status => status.type === type) ||
			OFFICER_STATUSES.none;
		return (
			<If
				condition={
					officerStatus.type !== OFFICER_STATUSES.none.type &&
					officerStatus.type !== OFFICER_STATUSES.seekingReplacement.type
				}
			>
				<div>
					<div className={`officer-status label label-${officerStatus.color}`}>
						{officerStatus.description}
					</div>
				</div>
			</If>
		);
	};

	/** Assigns an officer who not signed up on RollKall to the currently selected position */
	assignExternalOfficer = () => {
		const { lang } = global;
		const {
			position: { value: jobId },
			NonRKAssign: officerName,
		} = this.state;
		const { successCallback } = this.props;

		this.setState({ loading: true });
		ApplicantService.assignExternalOfficer({ officerName, jobId })
			.then(() => {
				this.setState(prevState => ({
					loading: false,
					NonRKAssign: "",
					assignmentsChanged: true,
					totalExternalOfficers: prevState.totalExternalOfficers + 1,
				}));
				utils.notify({
					type: "success",
					message: lang.t("Officer assigned successfully"),
					title: "Success",
				});
				this.getAssignedAndAppliedOfficers();
				successCallback(jobId);
			})
			.catch(() => {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: lang.t("Error assigning Officer"),
					title: "Error",
				});
			});
	};

	/**
	 * Un-assigns a non-RollKall officer from the currently selected position
	 * @param {Number} officerId The ID number assigned to the non-RollKall officer
	 */
	unAssignExternalOfficer = officerId => {
		const { lang } = global;
		const {
			position: { value: jobId },
		} = this.state;
		const { successCallback } = this.props;

		this.setState({ loading: true });
		ApplicantService.unAssignExternalOfficer(officerId)
			.then(() => {
				this.setState(prevState => ({
					loading: false,
					assignmentsChanged: true,
					totalExternalOfficers: prevState.totalExternalOfficers - 1,
				}));
				utils.notify({
					type: "success",
					message: lang.t("Officer unassigned successfully"),
					title: "Success",
				});
				this.getAssignedAndAppliedOfficers();
				successCallback(jobId);
			})
			.catch(() => {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: lang.t("Error unassigning Officer"),
					title: "Error",
				});
			});
	};

	/**
	 * Handles an assignment action taken on a RollKall officer.
	 * - If the officer _is not_ currently assigned to the selected position and the job is a recurring one, a confirmation modal is shown.
	 * - If the officer _is not_ currently assigned to the selected position, an API request to assign them is made immediately.
	 * - If the officer _is_ currently assigned to the selected position, a modal is open prompting the user for a reason they chose to unassign the officer.
	 * @param {Object} officer Data object representing a RollKall officer
	 */
	handleRKAssignAction = officer => {
		const { recurrenceRule } = this.props;

		if (!this.isOfficerAssigned(officer.UserId || officer.Id)) {
			if (recurrenceRule && recurrenceRule.Id) {
				this.openAssignmentConfirmationModal(officer);
			} else {
				this.assignRKOfficer(officer.UserId || officer.Id);
			}
		} else {
			this.openUnassignmentModal(officer, recurrenceRule);
		}
	};

	/**
	 * Makes an API request to assign a RollKall officer to the currently selected position.
	 * Assignment conflicts are handled by other methods such as {@link onOfficerHoursLimitReached} and {@link reassignRKOfficer}.
	 * Upon successful assignment, the updated position ID is passed to the prop `successCallback` for further handling if needed.
	 * @param {Number} userId The user ID number for the officer to assign
	 * @param {Boolean} ignoreMaxWeeklyHrs Whether or not an officer's maximum weekly hour limit should be ignored (if present)
	 * @param {Boolean} overruleAssignment Whether or not this assignment should overrule any existing scheduling conflicts
	 * @param {Boolean} userScheduleConflictOverride Whether or not this assignment should override any user schedule conflicts
	 */
	assignRKOfficer = (
		userId,
		ignoreMaxWeeklyHrs,
		overruleAssignment,
		userScheduleConflictOverride = false,
	) => {
		const { lang } = global;
		const {
			position: { value: jobId },
		} = this.state;
		const { successCallback } = this.props;

		this.setState({ loading: true });
		ApplicantService.assignOfficer(
			{ ignoreMaxWeeklyHrs, userScheduleConflictOverride },
			{ userId, jobId, overruleAssignment },
		)
			.then(() => {
				this.setState({ loading: false, assignmentsChanged: true });
				utils.notify({
					type: "success",
					message: lang.t("Officer assigned successfully"),
					title: "Success",
				});
				this.getAssignedAndAppliedOfficers();
				successCallback(jobId);
			})
			.catch(({ response: { status, data } }) => {
				this.setState({ loading: false });
				if (status === 409) {
					if (
						data.ConflictType ===
							RECURRENCE_CONFLICT_TYPES.CURRENT_USER_SCHEDULE_COLLISIONS.value ||
						data.ConflictType ===
							RECURRENCE_CONFLICT_TYPES.FUTURE_USER_SCHEDULE_COLLISIONS.value ||
						data.ConflictType ===
							RECURRENCE_CONFLICT_TYPES.CURRENT_FUTURE_USER_SCHEDULE_COLLISIONS.value
					) {
						this.setState({ alreadyOverrideUserScheduleConflict: false });
						this.onUserScheduleCollision({
							userId,
							ignoreMaxWeeklyHrs,
							message: data.Message,
							jobSupervisorCanOverride: data.OverrideIsAllowed,
							IsExternalOfficer: data.IsExternalOfficer,
						});
					} else if (
						data.ConflictType ===
							RECURRENCE_CONFLICT_TYPES.CURRENT_JOB_MAX_HOURS_REACHED.value ||
						data.ConflictType ===
							RECURRENCE_CONFLICT_TYPES.FUTURE_JOB_MAX_HOURS_REACHED.value ||
						data.ConflictType ===
							RECURRENCE_CONFLICT_TYPES.CURRENT_FUTURE_MAX_HOURS_REACHED.value
					) {
						this.onOfficerHoursLimitReached({
							userId,
							overruleAssignment,
							positionId: jobId,
							message: data.Message,
						});
					} else if (
						data.ConflictType ===
							RECURRENCE_CONFLICT_TYPES.CURRENT_JOB_COLLISIONS.value ||
						data.ConflictType ===
							RECURRENCE_CONFLICT_TYPES.FUTURE_JOB_COLLISIONS.value ||
						data.ConflictType ===
							RECURRENCE_CONFLICT_TYPES.CURRENT_FUTURE_JOB_COLLISIONS.value
					) {
						this.reassignRKOfficer({
							userId,
							ignoreMaxWeeklyHrs,
							message: data.JobEvents[0]?.Message || "",
						});
					}
				} else {
					this.setState({ alreadyOverrideUserScheduleConflict: false });
					utils.notify({
						type: "error",
						message: data.Message || lang.t("Error assigning Officer"),
						timeOut: 2000,
					});
				}
			});
	};

	/**
	 * Makes an API request to assign a RollKall officer to the currently selected position on a recurring basis.
	 * Assignment conflicts are handled by other methods such as {@link onOfficerHoursLimitReached} and {@link reassignRecurringRKOfficer}.
	 * Upon successful assignment, the updated position ID is passed to the prop `successCallback` for further handling if needed.
	 * @param {Object} params properties that may be required when assigning an officer to a recurring position.
	 * All properties of params have a default value except for `userId`, which is the user ID number of the officer to assign.
	 */
	assignRecurringRKOfficer = params => {
		const { lang } = global;
		const {
			position: { value: jobId },
			assignConfirmModal: { show: isACMVisible },
			conflictConfirmModal: { show: isCCMVisible },
		} = this.state;
		const { successCallback } = this.props;
		const {
			userId,
			ignoreMaxWeeklyHrs = false,
			updateAllUpcoming = false,
			OverRuleAssignment = false,
			OverRuleFutureJobAssignments = false,
			AssignmentRepeatsOn = "",
			userScheduleConflictOverride = false,
			SkipFutureJobAssignmentErrors = false,
		} = params;
		const isModalVisible = isACMVisible || isCCMVisible;

		this.setState({ loading: true });
		ApplicantService.assignOfficer(
			{ updateAllUpcoming, ignoreMaxWeeklyHrs, userScheduleConflictOverride },
			{
				userId,
				jobId,
				OverRuleAssignment,
				OverRuleFutureJobAssignments,
				AssignmentRepeatsOn,
				SkipFutureJobAssignmentErrors,
			},
		)
			.then(() => {
				this.setState(prevState => ({
					loading: false,
					hidden: isModalVisible ? false : prevState.hidden,
					assignConfirmModal: isACMVisible
						? this.initialAssignConfirmModal
						: prevState.assignConfirmModal,
					conflictConfirmModal: isCCMVisible
						? this.initialConflictConfirmModal
						: prevState.conflictConfirmModal,
					assignmentsChanged: true,
				}));
				utils.notify({
					type: "success",
					message: lang.t("Officer assigned successfully"),
					title: "Success",
				});
				this.getAssignedAndAppliedOfficers();
				successCallback(jobId);
			})
			.catch(({ response }) => {
				this.setState({ loading: false });
				if (!response) {
					// Reset loading and modal states as needed just like in a successful scenario
					this.setState(prevState => ({
						loading: false,
						hidden: isModalVisible ? false : prevState.hidden,
						assignConfirmModal: isACMVisible
							? this.initialAssignConfirmModal
							: prevState.assignConfirmModal,
						conflictConfirmModal: isCCMVisible
							? this.initialConflictConfirmModal
							: prevState.conflictConfirmModal,
					}));
					utils.notify({
						type: "error",
						message: lang.t("Error assigning Officer"),
						timeOut: 2000,
					});
				} else {
					const { data, status } = response;
					// Check if all the jobs are already filled by searching the message string for particular text
					const areJobsFilled =
						data.jobEvents &&
						data.JobEvents.filter(({ Message }) =>
							(Message || "").includes("already been filled"),
						).length === data.JobEvents.length;
					if (status === 409 && !areJobsFilled) {
						// Reset loading and modal states so that a new one can be opened, but keep this modal hidden
						// Two modals should not be open at the same time
						this.setState(
							prevState => ({
								loading: false,
								assignConfirmModal: isACMVisible
									? this.initialAssignConfirmModal
									: prevState.assignConfirmModal,
								conflictConfirmModal: isCCMVisible
									? this.initialConflictConfirmModal
									: prevState.conflictConfirmModal,
							}),
							() => {
								if (
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES.CURRENT_JOB_MAX_HOURS_REACHED
											.value ||
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES.FUTURE_JOB_MAX_HOURS_REACHED
											.value ||
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES.CURRENT_FUTURE_MAX_HOURS_REACHED
											.value
								) {
									this.onOfficerHoursLimitReached({
										userId,
										updateAllUpcoming,
										AssignmentRepeatsOn,
										overruleAssignment: OverRuleAssignment,
										positionId: jobId,
										message: data.Message,
									});
								} else if (
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES.CANNOT_ASSIGN_FUTURE_JOBS.value ||
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES.CURRENT_USER_SCHEDULE_COLLISIONS
											.value ||
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES.FUTURE_USER_SCHEDULE_COLLISIONS
											.value ||
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES
											.CURRENT_FUTURE_USER_SCHEDULE_COLLISIONS.value ||
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES.CURRENT_JOB_COLLISIONS.value ||
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES.FUTURE_JOB_COLLISIONS.value ||
									data.ConflictType ===
										RECURRENCE_CONFLICT_TYPES.CURRENT_FUTURE_JOB_COLLISIONS
											.value
								) {
									this.reassignRecurringRKOfficer({
										userId,
										ignoreMaxWeeklyHrs,
										updateAllUpcoming,
										AssignmentRepeatsOn,
										conflictType: data.ConflictType,
										jobEvents: data.JobEvents,
										userScheduleConflictOverride:
											data.OverrideIsAllowed ?? true,
									});
								}
							},
						);
					} else {
						// Reset loading and modal states as needed just like in a successful scenario
						this.setState(prevState => ({
							loading: false,
							hidden: isModalVisible ? false : prevState.hidden,
							assignConfirmModal: isACMVisible
								? this.initialAssignConfirmModal
								: prevState.assignConfirmModal,
							conflictConfirmModal: isCCMVisible
								? this.initialConflictConfirmModal
								: prevState.conflictConfirmModal,
						}));
						// Show a specific error message if all selected jobs are already filled.
						// Otherwise, the user will not know why the assignment failed.
						if (areJobsFilled) {
							utils.notify({
								type: "error",
								message: lang.t("The selected jobs have already been filled_"),
								timeOut: 2000,
							});
						} else {
							utils.notify({
								type: "error",
								message: data.Message || lang.t("Error assigning Officer"),
								timeOut: 2000,
							});
						}
					}
				}
			});
	};

	/**
	 * Displays a modal indicating that an officer has reached their weekly off-duty hour limit. If the officer belongs to the same jurisdiction
	 * as the current user, they will be asked if they would like to override this rule. Otherwise, they are simply informed
	 * of the conflict and cannot proceed further.
	 * @param {Object} data Information used to handle a maximum hour limit conflict
	 */
	onOfficerHoursLimitReached = data => {
		const { lang } = global;
		const {
			RKOfficers: {
				results: [...officers],
			},
		} = this.state;
		const { recurrenceRule, jurisdictionId, isLEA, isSuperAdmin, isStaffAdmin } = this.props;
		const { updateAllUpcoming, overruleAssignment, AssignmentRepeatsOn, userId } = data;
		const officer = officers.find(({ Id }) => Id === userId);
		const isDifferentJurisdiction = officer?.JurisdictionId !== jurisdictionId;
		const canOverrideHourlyLimits =
			!isDifferentJurisdiction && (isSuperAdmin || (isLEA && isStaffAdmin));

		this.setState({ hidden: true });

		const stringArr = data.message.split("\r");
		const errorMessage = (
			<div>
				{stringArr.map(item => (
					<p
						className="error-message"
						key={item}
					>
						{item}
					</p>
				))}
			</div>
		);

		this.confirmationModal.current.openModal({
			title: lang.t("Hourly Limits Exceeded"),
			message: errorMessage,
			hideCancel: !canOverrideHourlyLimits,
			confirmButton: canOverrideHourlyLimits ? lang.t("Proceed") : lang.t("Cancel"),
			onCancel: () => this.setState({ hidden: false }),
			onConfirm: () => {
				this.setState({ hidden: false });
				if (!canOverrideHourlyLimits) return;
				if (updateAllUpcoming) {
					this.assignRecurringRKOfficer({
						userId,
						overruleAssignment,
						AssignmentRepeatsOn,
						ignoreMaxWeeklyHrs: true,
						RepeatsOn: recurrenceRule?.RepeatsOn || undefined,
						updateAllUpcoming,
					});
				} else {
					this.assignRKOfficer(userId, true, overruleAssignment);
				}
			},
		});
	};

	/**
	 * Displays a modal indicating that an officer has a scheduling conflict with their external schedule.
	 * If the jurisdiction allows the supervisor to override then they can resubmit.
	 * @param {Object} data Information used to handle the user schedule conflict
	 */
	onUserScheduleCollision = data => {
		const { lang } = global;
		const { jobSupervisorCanOverride } = data;
		this.setState({ hidden: true });
		if (data.IsExternalOfficer === true) {
			this.confirmationModal.current.openModal({
				enforceFocus: false,
				title: lang.t("Personnel Schedule Conflict"),
				message: lang.t(
					"This external personnel is assigned to an on duty job at this time and cannot be assigned to this position_",
				),
				hideCancel: true,
				onConfirm: () => {
					this.setState({ hidden: false });
				},
			});
		} else if (jobSupervisorCanOverride === true) {
			this.confirmationModal.current.openModal({
				enforceFocus: false,
				title: lang.t("Personnel Schedule Conflict"),
				message: `${lang.t("This personnel is assigned to an on-duty job that overlaps with this assignment_")} ${lang.t("Would you like to proceed with this assignment_")}`,
				confirmButton: `${lang.t("Yes")}, ${lang.t("Assign Anyway")}`,
				onCancel: () => this.setState({ hidden: false }),
				onConfirm: () => {
					this.setState({ hidden: false });
					this.handleOnUserScheduleCollisionConfirmation(data);
				},
			});
		} else {
			this.confirmationModal.current.openModal({
				enforceFocus: false,
				title: lang.t("Personnel Schedule Conflict"),
				message: `${lang.t("This personnel is assigned to an on-duty job that overlaps with this assignment_")} ${lang.t("Please contact an LEA admin to make this assignment_")}`,
				hideCancel: true,
				onConfirm: () => {
					this.setState({ hidden: false });
				},
			});
		}
	};

	handleOnUserScheduleCollisionConfirmation = data => {
		const { userId, ignoreMaxWeeklyHrs } = data;
		this.setState({ alreadyOverrideUserScheduleConflict: true });
		this.assignRKOfficer(userId, ignoreMaxWeeklyHrs, false, true);
	};

	/**
	 * Displays a modal indicating that an officer has reached their weekly off-duty hour limit. If the officer belongs to the same jurisdiction
	 * as the current user and is a LEA Admin or Super Admin they will be asked if they would like to override this rule. Otherwise, they are simply informed
	 * of the conflict and cannot proceed further.
	 * @param {Object} data Information used to handle a maximum hour limit conflict
	 * @param {String} message Error message to display
	 */
	onAcceptOfficerHoursLimitReached = (data, message) => {
		const { lang } = global;
		const { jurisdictionId, isLEA, isSuperAdmin, isStaffAdmin } = this.props;
		const { accept, applicant, jobId, userId } = data;
		const isDifferentJurisdiction = applicant?.User?.JurisdictionId !== jurisdictionId;
		const canOverrideHourlyLimits =
			!isDifferentJurisdiction && (isSuperAdmin || (isLEA && isStaffAdmin));

		this.setState({ hidden: true });
		this.confirmationModal.current.openModal({
			title: lang.t("Hourly Limits Exceeded"),
			message:
				message ||
				lang.t(
					"Assigning this officer to this job will exceed their allowable number of hours_",
				),
			hideCancel: !canOverrideHourlyLimits,
			confirmButton: canOverrideHourlyLimits ? lang.t("Proceed") : lang.t("Cancel"),
			onCancel: () => this.setState({ hidden: false }),
			onConfirm: () => {
				this.setState({ hidden: false });
				if (!canOverrideHourlyLimits) return;
				this.handleApplicantStatusChange({ UserId: userId, JobId: jobId }, accept, true);
			},
		});
	};

	/**
	 * Displays a modal indicating that an officer has a scheduling conflict with another job. If the user confirms that they would like to override the
	 * officer's current assignment, the officer is un-assigned from their scheduled job and assigned to this one instead.
	 * @param {Object} data Information used to handle a scheduling conflict
	 */
	reassignRKOfficer = data => {
		const { lang } = global;
		const { alreadyOverrideUserScheduleConflict } = this.state;
		const { message, userId, ignoreMaxWeeklyHrs } = data;
		const userScheduleConflictOverride = alreadyOverrideUserScheduleConflict;
		this.setState({ hidden: true });
		this.reassignConfirmationModal.current.openModal({
			enforceFocus: false,
			title: lang.t("Reassign Officer"),
			message,
			confirmButton: `${lang.t("Yes")}, ${lang.t("Reassign Officer")}`,
			onCancel: () => this.setState({ hidden: false }),
			onConfirm: () => {
				this.setState({ hidden: false });
				this.assignRKOfficer(
					userId,
					ignoreMaxWeeklyHrs,
					true,
					userScheduleConflictOverride,
				);
			},
		});
	};

	/**
	 * Displays the `<ConflictConfirmationModal>` component to aid in resolving the scheduling conflict for the selected officer
	 * @param {Object} data Information used to handle a scheduling conflict
	 */
	reassignRecurringRKOfficer = data => {
		const {
			recurrenceRule: { RepeatsOn: days },
		} = this.props;
		const {
			userId,
			ignoreMaxWeeklyHrs,
			updateAllUpcoming,
			AssignmentRepeatsOn,
			conflictType,
			jobEvents,
			userScheduleConflictOverride,
		} = data;
		this.setState({
			conflictConfirmModal: {
				show: true,
				AssignmentRepeatsOn: AssignmentRepeatsOn || days,
				userId,
				updateAllUpcoming,
				ignoreMaxWeeklyHrs,
				conflictType,
				jobEvents,
				userScheduleConflictOverride,
			},
		});
	};

	/**
	 * Opens the assignment confirmation modal where the user is asked on what recurring basis an officer should
	 * be assigned to a job (either one instance, each recurrence, or certain days of the week)
	 * @param {Object} officer Data object representing a RollKall officer
	 */
	openAssignmentConfirmationModal = officer => {
		const { recurrenceRule } = this.props;
		let { FirstName, LastName, Id } = officer;
		if (officer.OfficerProfile)
			({
				OfficerProfile: { FirstName, LastName },
				UserId: Id,
			} = officer);
		this.setState({
			hidden: true,
			assignConfirmModal: {
				show: true,
				recurrenceRule,
				officer: { Id, FirstName, LastName },
			},
		});
	};

	/**
	 * Opens the unassignment modal so the user can indicate why they are un-assigning an officer
	 * @param {Object} officer Data object representing a RollKall officer
	 * @param {Object} recurrenceRule Data object representing the recurrence rule for the currently selected position
	 */
	openUnassignmentModal = (officer, recurrenceRule) => {
		// Hide the current modal, then open the new one
		let { FirstName, LastName, Id: officerId } = officer;
		if (officer.OfficerProfile)
			({
				OfficerProfile: { FirstName, LastName },
				UserId: officerId,
			} = officer);
		this.setState({
			hidden: true,
			unassignModal: {
				officerId,
				officerName: `${FirstName} ${LastName}`,
				show: true,
				recurringAssignment: this.isRecurringAssignment(officerId),
				recurrenceRule,
			},
		});
	};

	/**
	 * Closes the unassignment modal and calls the prop `successCallback` passing the current position ID as a parameter
	 * @param {Boolean} assignmentsChanged Indicates whether any assignments have changed upon closing the un-assignment modal. Defaults to `false`.
	 */
	closeUnassignmentModal = (assignmentsChanged = false) => {
		// This should ideally refresh data based on the page where this modal is called
		const {
			position: { value },
		} = this.state;
		const { successCallback } = this.props;
		this.getAssignedAndAppliedOfficers();
		// If officer(s) are unassigned, this.state.assignmentsChanged will be true. Otherwise, it will retain its value
		this.setState(
			prevState => ({
				hidden: false,
				unassignModal: this.initialUnassignModal,
				assignmentsChanged: assignmentsChanged || prevState.assignmentsChanged,
			}),
			() => successCallback(value),
		);
	};

	/** Closes the assignment confirmation modal and calls the prop `successCallback` passing the current position ID as a parameter */
	closeAssignmentConfirmationModal = () => {
		// This should ideally refresh data based on the page where this modal is called
		const {
			position: { value },
		} = this.state;
		const { successCallback } = this.props;
		this.getAssignedAndAppliedOfficers();
		this.setState({ hidden: false, assignConfirmModal: this.initialAssignConfirmModal }, () =>
			successCallback(value),
		);
	};

	/** Closes the conflict confirmation modal and calls the prop `successCallback` passing the current position ID as a parameter */
	closeConflictConfirmationModal = () => {
		// This should ideally refresh data based on the page where this modal is called
		const {
			position: { value },
		} = this.state;
		const { successCallback } = this.props;
		this.getAssignedAndAppliedOfficers();
		this.setState(
			{ hidden: false, conflictConfirmModal: this.initialConflictConfirmModal },
			() => successCallback(value),
		);
	};

	/** Clears current officer search results and displays officers assigned to the currently selected position in its place */
	clearSearchResults = () => {
		this.setState({
			RKOfficerFilter: "",
			prevSearchTerm: "",
			RKOfficers: {
				searched: false,
				loading: false,
				pageNumber: 1,
				results: [],
				hasMore: false,
			},
		});
	};

	setActiveTab = tab => {
		const { activeTab } = this.state;
		if (tab !== activeTab) this.setState({ activeTab: tab });
	};

	/**
	 * Determines whether or not an officer's application to work a job has been reviewed or not
	 * @param {Object} applicant Data object representing an applicant for the current position
	 * @returns {Boolean} `true` if their application is pending review; `false` otherwise
	 */
	isApplicationPending = applicant =>
		applicant.ApprovedDateTimeUTC === null &&
		applicant.DenialDateTimeUTC === null &&
		!applicant.Deleted;

	/**
	 * Makes an API request to update the status of an officer's application for the currently selected position
	 * @param {Object} applicant Data object representing an applicant for the current position
	 * @param {Boolean} accept Whether the application has been accepted or not
	 * @param {Boolean} ignoreMaxWeeklyHrs Flag to override hourly limits
	 */
	handleApplicantStatusChange = (applicant, accept, ignoreMaxWeeklyHrs = false) => {
		const { lang } = global;
		const { successCallback } = this.props;
		const params = {
			accept,
			applicant,
			userId: applicant.UserId,
			jobId: applicant.JobId,
		};
		ApplicantService.update(params, { ignoreMaxWeeklyHrs })
			.then(() => {
				this.setState({ assignmentsChanged: true });
				this.getAssignedAndAppliedOfficers();
				successCallback(params.jobId);
			})
			.catch(({ response: { status, data } }) => {
				if (
					status === 409 &&
					(data.ConflictType ===
						JOB_ASSIGNMENT_ERROR_TYPES.MAX_WEEKLY_WORK_HOUR_REACHED.value ||
						data.ConflictType ===
							JOB_ASSIGNMENT_ERROR_TYPES.MAX_HOURS_REACHED_BASED_ON_NEW_RULES.value)
				) {
					this.onAcceptOfficerHoursLimitReached(params, data?.Message);
				} else {
					utils.notify({
						type: "error",
						message: data?.Message || lang.t("Error updating applicant status"),
						timeOut: 1500,
					});
				}
			});
	};

	onExited = () => {
		const { hidden } = this.state;
		// After the modal has been closed (not temporarily hidden), reset the local state
		if (!hidden) this.setState({ ...this.initialState });
	};

	render() {
		const { lang } = global;
		const {
			loading,
			hidden,
			position,
			assignedOfficers,
			appliedOfficers,
			searchRKOfficers,
			RKOfficerFilter,
			prevSearchTerm,
			RKOfficers,
			unassignModal,
			assignConfirmModal,
			conflictConfirmModal,
			NonRKAssign,
			activeTab,
			includeNonJurisdiction,
			assignmentsChanged,
			totalExternalOfficers,
		} = this.state;

		const {
			show,
			closeModal,
			isActiveJob,
			timeZoneId,
			isSuperAdmin,
			isLEA,
			positions,
			isAgencyW2,
			isPrepayJob,
			isRKPayJob,
			name,
			isViewOnly,
		} = this.props;

		const options = positions.map(({ Id: value, Title: label }) => ({ value, label }));
		const selectedPosition = positions.find(({ Id }) => Id === position.value) || {};
		const { OfficersNeeded, OfficersSeekingReplacementCount } = selectedPosition;
		const allowAssignAction =
			!isSuperAdmin &&
			!isViewOnly &&
			OfficersNeeded - assignedOfficers.length + OfficersSeekingReplacementCount > 0;

		return (
			<Fragment>
				<Modal
					name={name}
					show={show && !hidden}
					onExited={this.onExited}
					onHide={closeModal}
					id="manual-assign-modal"
				>
					<Modal.Header>
						<Modal.Title>{lang.t("Manual Assignment")}</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="row form-group">
							<div className="col-xs-6 col-sm-4">
								<div className="radio">
									<label htmlFor="rkOfficers_yes">
										<input
											type="radio"
											name="searchRKOfficers"
											id="rkOfficers_yes"
											data-test-id="jobs__rk-officers-yes"
											onChange={() =>
												this.setState({ searchRKOfficers: true })
											}
											checked={searchRKOfficers}
										/>
										{lang.t("RollKall Officers")}
									</label>
								</div>
							</div>
							<div className="col-xs-6 col-sm-4">
								<div className="radio">
									<label htmlFor="rkOfficers_no">
										<input
											type="radio"
											name="searchRKOfficers"
											id="rkOfficers_no"
											data-test-id="jobs__rk-officers-no"
											onChange={() =>
												this.setState({ searchRKOfficers: false })
											}
											disabled={isPrepayJob}
											checked={!searchRKOfficers}
										/>
										{lang.t("Non-RollKall Officers")}
									</label>
								</div>
							</div>
						</div>
						<If
							condition={
								!isRKPayJob &&
								!searchRKOfficers &&
								totalExternalOfficers === 0 &&
								!isAgencyW2
							}
						>
							<div className="alert bg-warning text-center">
								<i className="glyphicon glyphicon-info-sign" />
								&nbsp;
								{lang.t(
									"If an external officer is assigned to the job, invoices cannot be paid online_",
								)}
							</div>
						</If>
						<div className="row form-group">
							<div className="col-xs-12 form-group">
								<label htmlFor="position-select">{lang.t("Select Position")}</label>
								<ReactSelect
									options={options}
									id="position-select"
									value={position}
									onChange={this.handlePositionChange}
									dataTestId="jobs__position-select"
								/>
							</div>
							<div className="col-xs-6 col-sm-4">
								{`${assignedOfficers.length}/${OfficersNeeded} ${lang.t("Assigned")}`}
							</div>
						</div>
						<div className="row">
							<Choose>
								<When condition={searchRKOfficers}>
									<div className={`col-xs-12${isLEA ? " col-sm-8" : ""}`}>
										<label htmlFor="rk-officer-search">
											{lang.t("Search by officer name or email")}
										</label>
										<div className="input-group">
											<input
												className="form-control"
												name="RKOfficerFilter"
												id="rk-officer-search"
												data-test-id="jobs__rk-officer-search"
												value={RKOfficerFilter}
												onChange={this.handleInputChange}
												onKeyPress={({ key }) => {
													if (key === "Enter") this.searchOfficers();
												}}
											/>
											<span className="input-group-btn">
												<button
													id="search-officer-button"
													data-test-id="jobs__search-officer-button"
													type="button"
													aria-label="Search officers"
													className="btn btn-primary"
													onClick={this.searchOfficers}
												>
													<i className="glyphicon glyphicon-search" />
												</button>
											</span>
										</div>
									</div>
									<If condition={isLEA}>
										<div className="col-xs-12 col-sm-4 switch-container">
											<br />
											<Switch
												id="my-officers-switch"
												data-test-id="jobs__my-officers-switch"
												label={lang.t("My Officers Only")}
												on={!includeNonJurisdiction}
												onClick={() => this.toggleOwnOfficers()}
											/>
										</div>
									</If>
								</When>
								<Otherwise>
									<div className="col-xs-12">
										<label htmlFor="non-rk-assign">
											{lang.t("Enter Non-RollKall officer_s name")}
										</label>
									</div>
									<div className="col-xs-8 col-sm-10 form-group">
										<input
											className="form-control"
											name="NonRKAssign"
											id="non-rk-assign"
											data-test-id="jobs__non-rk-assign"
											value={NonRKAssign}
											onChange={this.handleInputChange}
											onKeyPress={({ key }) => {
												if (key === "Enter") this.assignExternalOfficer();
											}}
											disabled={!allowAssignAction}
										/>
									</div>
									<div className="col-xs-4 col-sm-2 form-group">
										<button
											id="assign-button"
											data-test-id="jobs__assign-button"
											type="button"
											className="btn btn-success btn-block"
											onClick={this.assignExternalOfficer}
											disabled={!allowAssignAction}
										>
											{lang.t("Assign")}
										</button>
									</div>
								</Otherwise>
							</Choose>
						</div>
						<Choose>
							<When condition={searchRKOfficers && RKOfficers.searched}>
								<div className="row spaced-row">
									<div className="col-xs-9 col-sm-10">
										<strong>{`Showing results for: ${prevSearchTerm}`}</strong>
									</div>
									<div className="col-xs-3 col-sm-2">
										<button
											id="clear-search-button"
											data-test-id="jobs__clear-search-button"
											type="button"
											aria-label="Clear search results"
											className="btn btn-link btn-block"
											onClick={this.clearSearchResults}
										>
											<i className="glyphicon glyphicon-remove text-danger" />
										</button>
									</div>
								</div>
							</When>
							<When condition={searchRKOfficers}>
								<div className="row tabs spaced-row">
									<div className="col-xs-6 col-sm-4">
										<button
											id="assigned-officers-tab"
											type="button"
											className={classNames("btn btn-link btn-block", {
												active: activeTab === "assigned",
											})}
											onClick={() => this.setActiveTab("assigned")}
										>
											{`${lang.t("Assigned Officers")} (${assignedOfficers.length})`}
										</button>
									</div>
									<div className="col-xs-6 col-sm-4">
										<button
											id="applicants-tab"
											type="button"
											className={classNames("btn btn-link btn-block", {
												active: activeTab === "applicants",
											})}
											onClick={() => this.setActiveTab("applicants")}
										>
											{`${lang.t("Applicants")} (${appliedOfficers.length})`}
										</button>
									</div>
									<div className="col-xs-6 col-sm-4">
										<button
											id="seeking-replacement-tab"
											type="button"
											className={classNames("btn btn-link btn-block", {
												active: activeTab === "seeking",
											})}
											onClick={() => this.setActiveTab("seeking")}
										>
											{`${lang.t("Seeking Replacement")} (${(assignedOfficers || []).filter(o => o.OfficerStatus === 12).length})`}
										</button>
									</div>
								</div>
							</When>
						</Choose>
						<If condition={searchRKOfficers}>
							<div className="officer-list">
								<If condition={RKOfficers.loading}>
									<div className="row text-center no-overflow">
										<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
									</div>
								</If>
								<Choose>
									<When
										condition={
											RKOfficers.searched && RKOfficers.loading === false
										}
									>
										<If condition={RKOfficers.results.length === 0}>
											<div className="row text-center text-muted">
												{lang.t("No results found")}
											</div>
										</If>
										<For
											each="officer"
											of={RKOfficers.results}
										>
											<div
												key={`result-officer-${officer.Id}`}
												id={`result-officer-${officer.Id}`}
												className="row officer"
											>
												<div className="col-xs-4 col-sm-3">
													<Image
														fallbackSrc="avatar"
														src={officer.ContactPhotoUrl}
														className="img-circle"
														alt={`${officer.FirstName} ${officer.LastName}'s profile picture`}
													/>
													<If
														condition={this.isRecurringAssignment(
															officer.Id,
														)}
													>
														<Tooltip
															content={lang.t(
																"This officer is assigned to multiple occurrences of this job",
															)}
														>
															<i className="rkf rkf-refresh" />
														</Tooltip>
													</If>
												</div>
												<div className="col-xs-8 col-sm-6">
													<strong className="text-capitalize">
														{officer.FirstName} {officer.LastName}
													</strong>
													<small>{`${lang.t("Rank")}: ${officer.OfficerRank}`}</small>
													<small>{`${lang.t("Jurisdiction")}: ${officer.Department}`}</small>
													<small>
														<i className="rkf rkf-phone" />
														&nbsp;
														{utils.formatPhoneNumber(
															officer.MobilePhone,
														)}
													</small>
													<small
														className="truncate"
														title={officer.EmailAddress}
													>
														<i className="rkf rkf-email" />
														&nbsp;
														{officer.EmailAddress}
													</small>
													<br />
													{officer.IsMinimumPayRate && (
														<small>
															*This officer has a minimum pay rate set
															by their department.
														</small>
													)}
												</div>
												<div className="col-xs-6 col-xs-offset-4 col-sm-3 col-sm-offset-0">
													<Choose>
														<When
															condition={
																allowAssignAction &&
																!this.isOfficerAssigned(officer.Id)
															}
														>
															<button
																id={`result-officer-assign-btn-${officer.Id}`}
																type="button"
																className="btn btn-block btn-success"
																onClick={() =>
																	this.handleRKAssignAction(
																		officer,
																	)
																}
															>
																{lang.t("Assign")}
															</button>
														</When>
														<When
															condition={
																allowAssignAction &&
																!isActiveJob &&
																this.isOfficerAssigned(officer.Id)
															}
														>
															<button
																id={`result-officer-unassign-btn-${officer.Id}`}
																type="button"
																className="btn btn-block btn-danger"
																onClick={() =>
																	this.handleRKAssignAction(
																		officer,
																	)
																}
															>
																{lang.t("Unassign")}
															</button>
														</When>
													</Choose>
												</div>
											</div>
										</For>
										<If condition={RKOfficers.hasMore}>
											<ScrollDetect
												className="text-center no-overflow"
												onVisible={this.getFilteredOfficers}
											>
												<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
											</ScrollDetect>
										</If>
									</When>
									<When
										condition={
											activeTab === "assigned" && RKOfficers.loading === false
										}
									>
										<If condition={assignedOfficers.length === 0}>
											<div
												id="no-officers-message"
												className="row text-center text-muted"
											>
												{lang.t("No officers assigned")}
											</div>
										</If>
										<For
											each="officer"
											of={assignedOfficers}
										>
											<div
												key={`assigned-${officer.isExternal ? officer.Id : officer.UserId}`}
												className="row officer"
											>
												<div className="col-xs-4 col-sm-3">
													<Image
														fallbackSrc="avatar"
														src={
															officer.OfficerProfile?.ContactPhotoUrl
														}
														className="img-circle"
														alt={
															officer.isExternal
																? `${officer.OfficerName} (${lang.t("Non-RollKall Officer")})`
																: `${officer.OfficerProfile.FirstName} ${officer.OfficerProfile.LastName}'s profile picture`
														}
													/>
													<If
														condition={this.isRecurringAssignment(
															officer.UserId,
														)}
													>
														<Tooltip
															content={lang.t(
																"This officer is assigned to multiple occurrences of this job",
															)}
														>
															<i className="rkf rkf-refresh" />
														</Tooltip>
													</If>
												</div>
												<div className="col-xs-8 col-sm-6">
													<Choose>
														<When condition={officer.isExternal}>
															<strong className="text-capitalize">
																{officer.OfficerName}
															</strong>
															<small className="text-muted">
																{lang.t("Non-RollKall Officer")}
															</small>
														</When>
														<Otherwise>
															<strong className="text-capitalize">
																{officer.OfficerProfile.FirstName}{" "}
																{officer.OfficerProfile.LastName}
																<If
																	condition={
																		officer.OfficerStatus === 12
																	}
																>
																	&nbsp;
																	<div className="label label-danger">
																		{lang.t(
																			"Seeking Replacement",
																		)}
																	</div>
																</If>
															</strong>
															<small>{`${officer.OfficerProfile.OfficerRank}, ${officer.OfficerProfile.JurisdictionName}`}</small>
															<small>
																<i className="rkf rkf-phone" />
																&nbsp;
																{utils.formatPhoneNumber(
																	officer.OfficerProfile
																		.CellPhone,
																)}
															</small>
															<small
																className="truncate"
																title={officer.OfficerProfile.Email}
															>
																<i className="rkf rkf-email" />
																&nbsp;
																{officer.OfficerProfile.Email}
															</small>
														</Otherwise>
													</Choose>
													<br />
													{officer.IsMinimumPayRate && (
														<small>
															*This officer has a minimum pay rate set
															by their department.
														</small>
													)}
												</div>
												<div className="col-xs-8 col-xs-offset-4 col-sm-3 col-sm-offset-0">
													<If
														condition={
															isActiveJob && !officer.isExternal
														}
													>
														<small>
															<Choose>
																<When condition={officer.ClockIn}>
																	{utils.formatTime(
																		new Date(
																			`${officer.ClockIn}Z`,
																		),
																		true,
																		timeZoneId,
																	)}
																</When>
																<Otherwise>--</Otherwise>
															</Choose>
															&nbsp;
															{lang.t("Clock In")}
														</small>
														<small>
															<Choose>
																<When condition={officer.ClockOut}>
																	{utils.formatTime(
																		new Date(
																			`${officer.ClockOut}Z`,
																		),
																		true,
																		timeZoneId,
																	)}
																</When>
																<Otherwise>--</Otherwise>
															</Choose>
															&nbsp;
															{lang.t("Clock Out")}
														</small>
														<div>
															<div className="label label-default">
																{`${utils.abbreviateNumber(officer.WorkedHours || 0)} Total Hours`}
															</div>
														</div>
														{this.renderOfficerStatus(
															officer.OfficerStatus,
														)}
													</If>
													{/* UserId for RK officers, Id as fallback for external officers */}
													<If
														condition={this.isOfficerAssigned(
															officer.UserId || officer.Id,
														)}
													>
														<button
															id={`unassign-btn${officer.isExternal ? officer.Id : officer.UserId}`}
															type="button"
															className="btn btn-block btn-danger"
															onClick={
																officer.isExternal
																	? () =>
																			this.unAssignExternalOfficer(
																				officer.Id,
																			)
																	: () =>
																			this.handleRKAssignAction(
																				officer,
																			)
															}
															disabled={isViewOnly}
														>
															{lang.t("Unassign")}
														</button>
													</If>
												</div>
											</div>
										</For>
									</When>
									<When
										condition={
											activeTab === "applicants" &&
											RKOfficers.loading === false
										}
									>
										<If condition={appliedOfficers.length === 0}>
											<div className="row text-center text-muted">
												{lang.t("No applicants received")}
											</div>
										</If>
										<For
											each="applicant"
											of={appliedOfficers}
										>
											<div
												key={`applied-${applicant.Id}`}
												className="row officer"
											>
												<div className="col-xs-4 col-sm-2">
													<Image
														className="profile-img img-circle"
														src={applicant.User.ContactPhotoUrl || ""}
														fallbackSrc="avatar"
													/>
												</div>
												<div className="col-xs-8 col-sm-6">
													<strong className="text-capitalize">
														{`${applicant.User.FirstName || ""} ${applicant.User.LastName || ""}`}
													</strong>
													<If condition={applicant.CreateDateUTC}>
														<small className="text-muted">
															{utils.formatDate(
																new Date(
																	`${applicant.CreateDateUTC}Z`,
																),
																"en-US",
																timeZoneId,
																"2-digit",
															)}
															&nbsp;
															{utils.formatTime(
																new Date(
																	`${applicant.CreateDateUTC}Z`,
																),
																true,
																timeZoneId,
															)}
														</small>
													</If>
													<small>
														{applicant.User.OfficerRank || ""}
														{applicant.User.OfficerRank &&
														applicant.User.JurisdictionName
															? ", "
															: ""}
														{applicant.User.JurisdictionName || ""}
													</small>
													<If condition={applicant.User.CellPhone}>
														<small>
															<i className="rkf rkf-phone" />
															&nbsp;
															{utils.formatPhoneNumber(
																applicant.User.CellPhone,
															)}
														</small>
													</If>
													<If condition={applicant.User.Email}>
														<small
															className="truncate"
															title={applicant.User.Email}
														>
															<i className="rkf rkf-email" />
															&nbsp;
															{applicant.User.Email}
														</small>
													</If>
												</div>
												<div className="col-xs-12 col-sm-3 statuses">
													<Choose>
														<When condition={applicant.User.DoNotHire}>
															<span className="label label-danger">
																{lang.t(
																	"Officer is on Do Not Hire list",
																)}
															</span>
														</When>
													</Choose>
													<If condition={applicant.ApprovedDateTimeUTC}>
														<span className="label label-success">
															{applicant.FromRequest
																? "Approved"
																: "Manual"}
														</span>
													</If>
													<If
														condition={
															(applicant.DenialDateTimeUTC ||
																applicant.DeleteDateUTC) &&
															applicant.UnassignmentReason !== 1
														}
													>
														<span className="label label-danger">
															{lang.t("Denied")}
														</span>
													</If>
													<If
														condition={
															applicant.ApprovedDateTimeUTC &&
															applicant.UnassignmentReason === 1
														}
													>
														<span className="label label-danger">
															{lang.t("Called Off")}
														</span>
													</If>
													<If
														condition={this.isApplicationPending(
															applicant,
														)}
													>
														<button
															type="button"
															className="btn btn-danger btn-block"
															onClick={() =>
																this.handleApplicantStatusChange(
																	applicant,
																	false,
																)
															}
															disabled={isViewOnly}
														>
															{lang.t("Decline")}
														</button>
														<button
															type="button"
															className="btn btn-success btn-block approve-app"
															onClick={() =>
																this.handleApplicantStatusChange(
																	applicant,
																	true,
																)
															}
															disabled={isViewOnly}
														>
															{lang.t("Accept")}
														</button>
													</If>
												</div>
											</div>
										</For>
									</When>
									<When
										condition={
											activeTab === "seeking" && RKOfficers.loading === false
										}
									>
										<If
											condition={
												assignedOfficers.filter(o => o.OfficerStatus === 12)
													.length === 0
											}
										>
											<div className="row text-center text-muted">
												{lang.t("No officers seeking replacement")}
											</div>
										</If>
										<For
											each="officer"
											of={(assignedOfficers || []).filter(
												o => o.OfficerStatus === 12,
											)}
										>
											<div
												key={`assigned-${officer.Id}`}
												className="row officer"
											>
												<div className="col-xs-4 col-sm-3">
													<Image
														fallbackSrc="avatar"
														src={
															officer.OfficerProfile?.ContactPhotoUrl
														}
														className="img-circle"
														alt={`${officer.OfficerProfile.FirstName} ${officer.OfficerProfile.LastName}'s profile picture`}
													/>
													<If
														condition={this.isRecurringAssignment(
															officer.UserId,
														)}
													>
														<Tooltip
															content={lang.t(
																"This officer is assigned to multiple occurrences of this job",
															)}
														>
															<i className="rkf rkf-refresh" />
														</Tooltip>
													</If>
												</div>
												<div className="col-xs-8 col-sm-6">
													<strong className="text-capitalize">
														{officer.OfficerProfile.FirstName}{" "}
														{officer.OfficerProfile.LastName}
													</strong>
													<small>{`${officer.OfficerProfile.OfficerRank}, ${officer.OfficerProfile.JurisdictionName}`}</small>
													<small>
														<i className="rkf rkf-phone" />
														&nbsp;
														{utils.formatPhoneNumber(
															officer.OfficerProfile.CellPhone,
														)}
													</small>
													<small
														className="truncate"
														title={officer.OfficerProfile.Email}
													>
														<i className="rkf rkf-email" />
														&nbsp;
														{officer.OfficerProfile.Email}
													</small>
												</div>
												<div className="col-xs-8 col-xs-offset-4 col-sm-3 col-sm-offset-0">
													<If
														condition={this.isOfficerAssigned(
															officer.UserId || officer.Id,
														)}
													>
														<button
															type="button"
															className="btn btn-block btn-danger"
															onClick={() =>
																this.handleRKAssignAction(officer)
															}
															disabled={isViewOnly}
														>
															{lang.t("Unassign")}
														</button>
													</If>
												</div>
											</div>
										</For>
									</When>
								</Choose>
							</div>
						</If>
					</Modal.Body>
					<Modal.Footer>
						<button
							id="im-done-button"
							type="button"
							className="btn btn-link"
							onClick={() => closeModal(assignmentsChanged)}
						>
							{lang.t("I_m done")}
						</button>
					</Modal.Footer>
					<Loader active={loading} />
				</Modal>
				<UnassignOfficerModal
					positionId={position.value}
					unassignProps={unassignModal}
					onClose={this.closeUnassignmentModal}
				/>
				<ConflictConfirmationModal
					conflictProps={conflictConfirmModal}
					onConfirm={this.assignRecurringRKOfficer}
					onClose={this.closeConflictConfirmationModal}
				/>
				<AssignmentConfirmationModal
					assignmentProps={assignConfirmModal}
					onConfirm={this.assignRecurringRKOfficer}
					onClose={this.closeAssignmentConfirmationModal}
				/>
				<ConfirmationModal ref={this.confirmationModal} />
				<ConfirmationModal ref={this.reassignConfirmationModal} />
			</Fragment>
		);
	}
}

ManualAssignModal.propTypes = {
	name: PropTypes.string,
	show: PropTypes.bool,
	defaultPosition: PropTypes.number,
	isActiveJob: PropTypes.bool,
	timeZoneId: PropTypes.string,
	jurisdictionId: PropTypes.number,
	isSuperAdmin: PropTypes.bool,
	isLEA: PropTypes.bool,
	isStaffAdmin: PropTypes.bool,
	positions: PropTypes.array,
	recurrenceRule: PropTypes.object,
	isAgencyW2: PropTypes.bool,
	isPrepayJob: PropTypes.bool,
	isRKPayJob: PropTypes.bool,
	successCallback: PropTypes.func,
	closeModal: PropTypes.func,
	isViewOnly: PropTypes.bool,
};

ManualAssignModal.defaultProps = {
	name: "",
	show: false,
	defaultPosition: undefined,
	isActiveJob: false,
	timeZoneId: undefined,
	jurisdictionId: undefined,
	isSuperAdmin: false,
	isLEA: false,
	isStaffAdmin: false,
	positions: [],
	recurrenceRule: undefined,
	isAgencyW2: false,
	isPrepayJob: false,
	isRKPayJob: false,
	successCallback: () => {},
	closeModal: () => {},
	isViewOnly: false,
};

export default ManualAssignModal;
